import React, { useCallback, useRef, useState } from 'react'
import MediaQuery from 'react-responsive'
import ReactSwipeableViews from 'react-swipeable-views'
import { mod } from 'react-swipeable-views-core'
import { virtualize } from 'react-swipeable-views-utils'
import { MqDesktop, MqTablet } from 'streamr-ui/css/consts'
import styled from 'styled-components'
import SectionID from '~shared/SectionID'
import Member from './Member'
import MemberListTitle from './MemberListTitle'

const SwipeableViews = virtualize(ReactSwipeableViews)

const Inner = styled.div`
    position: relative;
`

const List = styled.div`
    margin: 0 auto;
    max-width: 565px;
    padding: 0 3rem;

    .react-swipeable-view-container > div {
        box-sizing: border-box;
        padding: 0;
    }

    @media ${MqTablet} {
        padding: 0;

        .react-swipeable-view-container > div {
            padding: 0 10%;
        }
    }

    @media ${MqDesktop} {
        width: 45%;

        .react-swipeable-view-container > div {
            padding: 0 8%;
        }

        > div {
            transform: translate(-35%, 0);
        }
    }
`

const UnstyledTeamContainer = ({ className, team }) => {
    const [currentIndex, setCurrentIndex] = useState(0)

    const [state, setState] = useState({
        isSwitching: false,
        indexDelta: null,
    })

    const startIndexRef = useRef(null)

    const onSwitching = useCallback((index, type) => {
        if (type === 'move' && startIndexRef.current === null) {
            startIndexRef.current = index
        }
        if (type === 'end') {
            startIndexRef.current = null
        }

        setState({
            indexDelta:
                startIndexRef.current === null ? null : index - (startIndexRef.current || 0),
            isSwitching: type === 'move',
        })
    }, [])

    const onPictureClick = useCallback(
        (change) => () => {
            if (!Number.isNaN(change)) {
                setCurrentIndex((i) => i + change)
            }
        },
        [],
    )

    // We need a separate function for setting the index. SwipeableViews calles it with 2 arguments which
    // isn't supported by hooks.
    const setIndex = useCallback((value) => {
        setCurrentIndex(value)
    }, [])

    const { members } = team

    return (
        <div className={className}>
            <SectionID scope="about" value={team.id} />
            <Inner>
                <MemberListTitle>
                    <span>Team</span>
                </MemberListTitle>
                <List>
                    <MediaQuery minWidth={1480}>
                        {(matches) => (
                            <SwipeableViews
                                hysteresis={0.1}
                                index={currentIndex}
                                onChangeIndex={setIndex}
                                onSwitching={matches ? onSwitching : null}
                                resistance
                                slideRenderer={({ index }) => {
                                    const localIndex = mod(index, members.length)
                                    const member = members[localIndex]
                                    const ahead = index - currentIndex
                                    // eslint-disable-next-line max-len
                                    const highlightRatio =
                                        typeof state.indexDelta !== 'number'
                                            ? null
                                            : Math.min(1, Math.max(-1, -ahead + state.indexDelta))
                                    const dxRatio = -Math.min(
                                        0,
                                        highlightRatio || (ahead > 0 ? -1 : 0),
                                    )

                                    return (
                                        <Member
                                            active={ahead === 0}
                                            arrow={
                                                (ahead > 0 && 'right') ||
                                                (ahead < 0 && 'left') ||
                                                'none'
                                            }
                                            data={member}
                                            idle={!state.isSwitching}
                                            key={`${member.id}${index}`}
                                            memberNumber={localIndex + 1}
                                            onPictureClick={onPictureClick(ahead / Math.abs(ahead))}
                                            totalMembers={members.length}
                                            style={{
                                                transform: `translate(${dxRatio * 100}%, 0)`,
                                            }}
                                        />
                                    )
                                }}
                                slideStyle={{
                                    overflow: 'visible',
                                }}
                                style={{
                                    overflowX: 'visible',
                                }}
                                springConfig={{
                                    duration: '0.3s',
                                    easeFunction: 'ease',
                                    delay: '0s',
                                }}
                            />
                        )}
                    </MediaQuery>
                </List>
            </Inner>
        </div>
    )
}

const TeamContainer = styled(UnstyledTeamContainer)`
    backface-visibility: hidden;
    overflow: hidden;
    padding: 4em 0;

    :nth-child(even) {
        background-color: #f5f5f5;
    }

    @media ${MqTablet} {
        padding: 7em 0;
    }

    @media ${MqDesktop} {
        padding: 10.5em 0;
    }
`

export default TeamContainer
